/**
 * The score is the physics. Everything here is synthesized and driven by the same state as the picture:
 * distance to the hole, the local clock rate, and the act's phase. Nothing is loaded; it starts from a gesture.
 *
 * Beds (continuous):  sub rumble with a binaural beat, a detuned organ drone through a filter that closes with
 *                     redshift, the accretion roar (filtered noise that swirls and throbs), far-away air.
 * Clocks:             Earth ticks bright and steady on the left; your tick thuds on the right and follows your rate.
 * Events:             arrival toll, caption chime, release clunk, choice tension, let-go sub-drop and whoosh,
 *                     the crossing boom after which only Earth's clock is left, the hold's resolution.
 */
import type { Track } from '../data/music';

export type Blip = 'ok' | 'choice' | 'end' | 'warn';
export type AudioEvent = 'arrival' | 'caption' | 'dive' | 'choice' | 'resolve' | 'hold' | 'letgo' | 'cross' | 'ending-hold';

export interface SceneState {
  /** local clock rate, 0..1 */
  rate: number;
  /** distance from the centre in M units */
  dist: number;
  phase: string;
  /** azimuth of the orbit, radians; the hold's throb follows it */
  orbitPhase: number;
}

export interface Mix {
  sub: number; subFreq: number; drone: number; droneCut: number; roar: number; roarCut: number; roarThrob: number; air: number;
}

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));

/** Pure mapping from state to levels, so the curve can be tested. */
export function mix(dist: number, rate: number, phase: string): Mix {
  const prox = clamp((60 - dist) / 54, 0, 1);      // 0 far out, 1 at six radii
  const p2 = prox * prox;
  const falling = phase === 'fall';
  const dim = falling ? clamp(rate / 0.3, 0, 1) : 1; // the last stretch of the fall fades toward silence
  const holding = phase === 'hold';
  return {
    sub: (0.06 + 0.6 * p2) * (falling ? 0.6 + 0.4 * rate : 1),
    subFreq: 46 - 10 * prox,
    drone: (0.22 + 0.18 * prox) * dim,
    droneCut: (700 + 2600 * prox) * (0.25 + 0.75 * rate),
    roar: (0.015 + 0.75 * p2) * (0.5 + 0.5 * dim) * (holding ? 1.15 : 1),
    roarCut: (350 + 1500 * prox) * (0.4 + 0.6 * rate),
    roarThrob: holding ? 0.85 : 0.25 + 0.35 * p2,
    air: 0.07 * (1 - prox) * dim
  };
}

interface Voice { osc: OscillatorNode; base: number; gain: GainNode; }

/**
 * A tenth of a second of silence as a WAV data URI. On iOS, a media element that plays inside a tap moves the
 * page's audio session to "playback", and from then on the silent switch no longer mutes Web Audio.
 */
const SILENT_WAV = (() => {
  const rate = 8000, samples = rate / 10, bytes = new Uint8Array(44 + samples);
  const w = (o: number, v: number, n: number) => { for (let i = 0; i < n; i++) bytes[o + i] = (v >>> (8 * i)) & 255; };
  const tag = (o: number, s: string) => { for (let i = 0; i < s.length; i++) bytes[o + i] = s.charCodeAt(i); };
  tag(0, 'RIFF'); w(4, 36 + samples, 4); tag(8, 'WAVE'); tag(12, 'fmt '); w(16, 16, 4); w(20, 1, 2); w(22, 1, 2); w(24, rate, 4); w(28, rate, 4); w(32, 1, 2); w(34, 8, 2);
  tag(36, 'data'); w(40, samples, 4); bytes.fill(128, 44);
  let bin = ''; for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return 'data:audio/wav;base64,' + btoa(bin);
})();

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private kick: HTMLAudioElement | null = null;
  private resumeWired = false;
  private master!: GainNode;
  private beds!: GainNode;
  private reverbSend!: GainNode;
  private voices: Voice[] = [];
  private droneGain!: GainNode;
  private droneFilter!: BiquadFilterNode;
  private subGain!: GainNode;
  private subA!: OscillatorNode;
  private subB!: OscillatorNode;
  private roarGain!: GainNode;
  private roarFilter!: BiquadFilterNode;
  private roarThrob!: GainNode;
  private roarThrobLfo!: OscillatorNode;
  private roarPan!: StereoPannerNode;
  private roarPanLfo!: OscillatorNode;
  private airGain!: GainNode;
  private tension: { pedal: OscillatorNode; pedalGain: GainNode; dis: OscillatorNode; disGain: GainNode } | null = null;
  private rate = 1;
  private muted = true;
  private bedsOn = true;
  private lastOrbit = 0;
  private noiseBuf: AudioBuffer | null = null;
  private brownBuf: AudioBuffer | null = null;
  private analyser: AnalyserNode | null = null;
  // ---------- the soundtrack: a playlist that plays through the redshift rule ----------
  private playlist: Track[] = [];
  private trackIdx = -1;
  private buffers = new Map<string, AudioBuffer>();
  private pendingData = new Map<string, ArrayBuffer>();
  private cur: { src: AudioBufferSourceNode; gain: GainNode; startedAt: number; offset: number; idx: number } | null = null;
  private musicBus: GainNode | null = null;
  private musicVol = 0.75;
  private paused = false;
  private pausedOffset = 0;
  /** UI hook: called whenever the track or play state changes. */
  onTrack: ((title: string, playing: boolean) => void) | null = null;

  get hasMusic() { return this.playlist.length > 0; }
  get currentTrack(): Track | null { return this.trackIdx >= 0 ? this.playlist[this.trackIdx] : null; }
  get isPlaying() { return this.cur !== null && !this.paused; }

  /** Give the engine its playlist; the first two tracks are fetched right away. */
  setPlaylist(list: Track[]) {
    this.playlist = list;
    void this.prefetch(0); void this.prefetch(1);
  }

  private async prefetch(i: number) {
    const t = this.playlist[i]; if (!t || this.buffers.has(t.file) || this.pendingData.has(t.file)) return;
    try {
      const r = await fetch(t.file, { cache: 'force-cache' }); if (!r.ok) return;
      if ((r.headers.get('content-type') || '').includes('text/html')) return;   // a SPA fallback page, not audio
      const data = await r.arrayBuffer();
      if (this.ctx) this.buffers.set(t.file, await this.ctx.decodeAudioData(data)); else this.pendingData.set(t.file, data);
    } catch { /* no music for this track */ }
  }

  private async bufferFor(i: number): Promise<AudioBuffer | null> {
    const t = this.playlist[i]; if (!t || !this.ctx) return null;
    if (!this.buffers.has(t.file)) {
      if (!this.pendingData.has(t.file)) await this.prefetch(i);
      const data = this.pendingData.get(t.file);
      if (data) { try { this.buffers.set(t.file, await this.ctx.decodeAudioData(data)); } catch { /* undecodable */ } this.pendingData.delete(t.file); }
    }
    return this.buffers.get(t.file) ?? null;
  }

  /** Play track i (wrapping), crossfading from whatever is playing. */
  async play(i: number, crossfade = 2.5, offset = 0) {
    if (!this.ctx || this.playlist.length === 0) return;
    i = ((i % this.playlist.length) + this.playlist.length) % this.playlist.length;
    const buf = await this.bufferFor(i); if (!buf) return;
    const ctx = this.ctx, t = ctx.currentTime;
    if (!this.musicBus) { this.musicBus = ctx.createGain(); this.musicBus.gain.value = this.musicVol; this.musicBus.connect(this.beds); const send = ctx.createGain(); send.gain.value = 0.2; this.musicBus.connect(send); send.connect(this.reverbSend); }
    if (this.cur) { const old = this.cur; old.gain.gain.setTargetAtTime(0, t, crossfade / 3); old.src.onended = null; try { old.src.stop(t + crossfade + 0.2); } catch { /* already stopped */ } }
    const src = ctx.createBufferSource(); src.buffer = buf; src.loop = false; src.playbackRate.value = this.rate;
    const g = ctx.createGain(); g.gain.value = crossfade > 0 ? 0 : 1;
    src.connect(g); g.connect(this.musicBus);
    src.start(t, Math.max(0, Math.min(offset, buf.duration - 0.5)));
    if (crossfade > 0) g.gain.setTargetAtTime(1, t, crossfade / 3);
    const me = { src, gain: g, startedAt: t, offset, idx: i };
    src.onended = () => { if (this.cur === me && !this.paused) void this.play(i + 1, 1.5); };
    this.cur = me; this.trackIdx = i; this.paused = false;
    this.onTrack?.(this.playlist[i].title, true);
    void this.prefetch(i + 1);
  }

  /** The crossing fades every bed to silence; a new chapter, or a deliberate press on the player, brings them back. */
  wake() { this.bedsOn = true; }
  /** Cue track i for a chapter: a slow crossfade, and nothing if it is already playing. */
  cue(i: number) { this.wake(); if (i !== this.trackIdx && !this.paused) void this.play(i, 3.5); }
  next() { this.wake(); void this.play(this.trackIdx + 1, 1.5); }
  prev() { this.wake(); void this.play(this.trackIdx - 1, 1.5); }

  togglePause() {
    if (!this.ctx) return;
    if (this.cur && !this.paused) {
      const c = this.cur; this.paused = true;
      this.pausedOffset = c.offset + (this.ctx.currentTime - c.startedAt) * this.rate;
      c.src.onended = null; c.gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.15); try { c.src.stop(this.ctx.currentTime + 0.6); } catch { /* stopped */ }
      this.cur = null;
      this.onTrack?.(this.playlist[c.idx]?.title ?? '', false);
    } else if (this.paused) {
      void this.play(Math.max(0, this.trackIdx), 0.8, this.pausedOffset);
    } else {
      void this.play(Math.max(0, this.trackIdx), 1.5);
    }
  }

  setMusicVolume(v: number) {
    this.musicVol = clamp(v, 0, 1);
  }

  private startMusic() {
    if (!this.cur && !this.paused) void this.play(0, 4);
  }

  get started() { return this.ctx !== null; }
  get isMuted() { return this.muted; }
  get state() { return this.ctx ? this.ctx.state : 'none'; }

  /** RMS level of the master output in 0..1, for meters and tests. */
  level(): number {
    if (!this.analyser) return 0;
    const buf = new Float32Array(this.analyser.fftSize); this.analyser.getFloatTimeDomainData(buf);
    let s = 0; for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
    return Math.sqrt(s / buf.length);
  }

  /**
   * Phones: claim a playback session so the silent switch does not mute us (iOS 17+ has an API for it; older iOS
   * needs a media element to play inside the tap), and bring the context back whenever the phone hands it back
   * to us after a lock, a call or a switch to another app.
   */
  private claimPlayback() {
    const nav = navigator as Navigator & { audioSession?: { type: string } };
    try { if (nav.audioSession && nav.audioSession.type !== 'playback') nav.audioSession.type = 'playback'; } catch { /* not supported */ }
    if (!this.kick) {
      const a = document.createElement('audio');
      a.setAttribute('playsinline', ''); a.preload = 'auto'; a.volume = 0.01; a.src = SILENT_WAV;
      this.kick = a;
    }
    void this.kick.play().catch(() => { /* no media element playback here */ });
  }

  private wireResume() {
    if (this.resumeWired) return;
    this.resumeWired = true;
    const bump = () => { const c = this.ctx; if (c && (c.state as string) !== 'running' && (c.state as string) !== 'closed') void c.resume(); };
    for (const ev of ['pointerdown', 'touchend', 'keydown']) document.addEventListener(ev, bump, { passive: true });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') bump(); });
  }

  /** Build the graph. Call from a click or key handler. */
  start() {
    this.claimPlayback();
    if (this.ctx) { if ((this.ctx.state as string) !== 'running') void this.ctx.resume(); this.setMuted(false); return; }
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    const ctx: AudioContext = new AC();
    this.ctx = ctx;
    if ((ctx.state as string) !== 'running') void ctx.resume();
    this.wireResume();

    // master chain: beds + events -> compressor -> limiter -> master
    const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -20; comp.knee.value = 14; comp.ratio.value = 5; comp.attack.value = 0.004; comp.release.value = 0.3;
    const lim = ctx.createDynamicsCompressor(); lim.threshold.value = -4; lim.knee.value = 0; lim.ratio.value = 20; lim.attack.value = 0.001; lim.release.value = 0.12;
    this.master = ctx.createGain(); this.master.gain.value = 0;
    comp.connect(lim); lim.connect(this.master); this.master.connect(ctx.destination);
    this.analyser = ctx.createAnalyser(); this.analyser.fftSize = 1024; this.master.connect(this.analyser);
    const bus = comp;

    // reverb: a synthesized cavern
    const conv = ctx.createConvolver(); conv.buffer = this.makeImpulse(ctx, 4.2, 5.5); conv.normalize = true;
    const convGain = ctx.createGain(); convGain.gain.value = 0.55; conv.connect(convGain); convGain.connect(bus);
    this.reverbSend = ctx.createGain(); this.reverbSend.gain.value = 1; this.reverbSend.connect(conv);

    this.beds = ctx.createGain(); this.beds.gain.value = 1; this.beds.connect(bus);
    this.noiseBuf = this.makeNoise(ctx, 'white'); this.brownBuf = this.makeNoise(ctx, 'brown');

    // --- sub rumble: two sines beating against each other plus brown noise under a low-pass
    this.subGain = ctx.createGain(); this.subGain.gain.value = 0; this.subGain.connect(this.beds);
    this.subA = ctx.createOscillator(); this.subA.type = 'sine'; this.subA.frequency.value = 42; const ga = ctx.createGain(); ga.gain.value = 0.55; this.subA.connect(ga); ga.connect(this.subGain); this.subA.start();
    this.subB = ctx.createOscillator(); this.subB.type = 'sine'; this.subB.frequency.value = 46; const gb = ctx.createGain(); gb.gain.value = 0.35; this.subB.connect(gb); gb.connect(this.subGain); this.subB.start();
    const brown = this.loop(ctx, this.brownBuf); const bl = ctx.createBiquadFilter(); bl.type = 'lowpass'; bl.frequency.value = 95; bl.Q.value = 0.8; const bg = ctx.createGain(); bg.gain.value = 0.9;
    brown.connect(bl); bl.connect(bg); bg.connect(this.subGain);

    // --- organ drone: a minor voicing, each note a detuned pair, through a breathing low-pass
    this.droneFilter = ctx.createBiquadFilter(); this.droneFilter.type = 'lowpass'; this.droneFilter.frequency.value = 900; this.droneFilter.Q.value = 1.1;
    this.droneGain = ctx.createGain(); this.droneGain.gain.value = 0;
    this.droneFilter.connect(this.droneGain); this.droneGain.connect(this.beds);
    const droneVerb = ctx.createGain(); droneVerb.gain.value = 0.5; this.droneGain.connect(droneVerb); droneVerb.connect(this.reverbSend);
    const notes: [number, OscillatorType, number][] = [[55, 'sawtooth', 0.22], [82.41, 'sawtooth', 0.14], [110, 'triangle', 0.2], [130.81, 'sawtooth', 0.1], [164.81, 'triangle', 0.12], [220, 'sine', 0.08]];
    for (const [f, type, g] of notes) {
      for (const cents of [-6, 6]) {
        const osc = ctx.createOscillator(); osc.type = type; osc.frequency.value = f; osc.detune.value = cents;
        const gain = ctx.createGain(); gain.gain.value = g * 0.5;
        osc.connect(gain); gain.connect(this.droneFilter); osc.start();
        this.voices.push({ osc, base: f, gain });
      }
    }
    const breathe = ctx.createOscillator(); breathe.frequency.value = 0.06; const breatheG = ctx.createGain(); breatheG.gain.value = 180; breathe.connect(breatheG); breatheG.connect(this.droneFilter.frequency); breathe.start();
    const trem = ctx.createOscillator(); trem.frequency.value = 0.5; const tremG = ctx.createGain(); tremG.gain.value = 0.06; trem.connect(tremG); tremG.connect(this.droneGain.gain); trem.start();

    // --- accretion roar: band-passed noise, throbbing and swirling
    const white = this.loop(ctx, this.noiseBuf);
    this.roarFilter = ctx.createBiquadFilter(); this.roarFilter.type = 'bandpass'; this.roarFilter.frequency.value = 600; this.roarFilter.Q.value = 0.6;
    this.roarThrob = ctx.createGain(); this.roarThrob.gain.value = 0.7;
    this.roarThrobLfo = ctx.createOscillator(); this.roarThrobLfo.frequency.value = 0.3; const throbDepth = ctx.createGain(); throbDepth.gain.value = 0.3;
    this.roarThrobLfo.connect(throbDepth); throbDepth.connect(this.roarThrob.gain); this.roarThrobLfo.start();
    this.roarPan = ctx.createStereoPanner();
    this.roarPanLfo = ctx.createOscillator(); this.roarPanLfo.frequency.value = 0.07; const panDepth = ctx.createGain(); panDepth.gain.value = 0.8;
    this.roarPanLfo.connect(panDepth); panDepth.connect(this.roarPan.pan); this.roarPanLfo.start();
    this.roarGain = ctx.createGain(); this.roarGain.gain.value = 0;
    white.connect(this.roarFilter); this.roarFilter.connect(this.roarThrob); this.roarThrob.connect(this.roarPan); this.roarPan.connect(this.roarGain); this.roarGain.connect(this.beds);
    const roarVerb = ctx.createGain(); roarVerb.gain.value = 0.25; this.roarGain.connect(roarVerb); roarVerb.connect(this.reverbSend);

    // --- air: far-away shimmer
    const air = this.loop(ctx, this.noiseBuf); const ah = ctx.createBiquadFilter(); ah.type = 'highpass'; ah.frequency.value = 2500; const al = ctx.createBiquadFilter(); al.type = 'lowpass'; al.frequency.value = 7000;
    this.airGain = ctx.createGain(); this.airGain.gain.value = 0; air.connect(ah); ah.connect(al); al.connect(this.airGain); this.airGain.connect(this.beds);

    this.setMuted(false);
  }

  setMuted(m: boolean) {
    this.muted = m;
    if (this.ctx) this.master.gain.setTargetAtTime(m ? 0 : 0.85, this.ctx.currentTime, 0.3);
  }

  /** Local clock rate in (0, 1]; the drone and the roar shift by this ratio: gravitational redshift, audible. */
  setRate(rate: number) {
    this.rate = clamp(rate, 0.02, 1);
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    for (const v of this.voices) v.osc.frequency.setTargetAtTime(v.base * this.rate, t, 0.1);
  }

  /** Per-frame: move every bed toward the levels the state calls for. */
  setScene(s: SceneState) {
    this.setRate(s.rate);
    if (!this.ctx) return;
    const m = mix(s.dist, s.rate, s.phase), t = this.ctx.currentTime, k = 0.25, on = this.bedsOn ? 1 : 0;
    const withMusic = this.isPlaying ? 0.45 : 1;   // the organ drone steps back under a real score
    this.subGain.gain.setTargetAtTime(m.sub * on, t, k);
    this.subA.frequency.setTargetAtTime(m.subFreq, t, 0.5); this.subB.frequency.setTargetAtTime(m.subFreq + 4, t, 0.5);
    this.droneGain.gain.setTargetAtTime(m.drone * on * withMusic, t, k);
    if (this.cur) this.cur.src.playbackRate.setTargetAtTime(this.rate, t, 0.3);   // the score redshifts with your clock
    if (this.musicBus) {
      const dim = s.phase === 'fall' ? clamp(s.rate / 0.3, 0, 1) : 1;
      this.musicBus.gain.setTargetAtTime(this.musicVol * dim * (0.85 + 0.15 * (1 - m.roar)) * on, t, k);
    }
    this.droneFilter.frequency.setTargetAtTime(m.droneCut, t, k);
    this.roarGain.gain.setTargetAtTime(m.roar * on, t, k);
    this.roarFilter.frequency.setTargetAtTime(m.roarCut, t, k);
    this.roarThrob.gain.setTargetAtTime(m.roarThrob, t, k);
    this.airGain.gain.setTargetAtTime(m.air * on, t, k);
    if (s.phase === 'hold') {
      this.roarThrobLfo.frequency.setTargetAtTime(0.42, t, 0.5); this.roarPanLfo.frequency.setTargetAtTime(0.42, t, 0.5);
      // one low whomp per orbit
      const turn = Math.floor(s.orbitPhase / (Math.PI * 2));
      if (turn !== this.lastOrbit) { this.lastOrbit = turn; this.whomp(); }
    } else { this.roarThrobLfo.frequency.setTargetAtTime(0.3, t, 0.5); this.roarPanLfo.frequency.setTargetAtTime(0.07, t, 0.5); }
  }

  /** A clock tick. Earth ticks bright and steady on the left; the probe thuds on the right and follows your rate. */
  tick(kind: 'earth' | 'ship') {
    if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    if (kind === 'earth') {
      this.pluck(2200, 0.05, 0.04, 'triangle', -0.35, t);
      this.burst(1800, 4000, 0.03, 0.02, -0.35, t);
    } else {
      const f = 150 * (0.55 + 0.45 * this.rate);
      const o = this.ctx.createOscillator(), g = this.ctx.createGain(), p = this.ctx.createStereoPanner(); p.pan.value = 0.35;
      o.type = 'sine'; o.frequency.setValueAtTime(f * 1.6, t); o.frequency.exponentialRampToValueAtTime(f, t + 0.06);
      g.gain.setValueAtTime(0.16, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
      o.connect(g); g.connect(p); p.connect(this.beds); g.connect(this.reverbSend); o.start(t); o.stop(t + 0.13);
    }
  }

  blip(kind: Blip) {
    if (!this.ctx || this.muted) return;
    const k = this.rate, t = this.ctx.currentTime;
    if (kind === 'ok') { this.pluck(880 * k, 0.07, 0.2, 'sine', 0, t); this.pluck(1320 * k, 0.04, 0.3, 'sine', 0, t + 0.05); }
    else if (kind === 'choice') { this.pluck(220 * k, 0.12, 0.8, 'triangle', 0, t); this.pluck(330 * k, 0.08, 0.8, 'triangle', 0, t); }
    else if (kind === 'end') { this.pluck(110 * k, 0.16, 2.2, 'sine', 0, t); this.pluck(165 * k, 0.1, 2.2, 'sine', 0, t); this.pluck(220 * k, 0.06, 2.2, 'sine', 0, t); }
    else { this.pluck(196 * k, 0.1, 0.35, 'triangle', 0, t); }
  }

  /** Story beats. */
  event(e: AudioEvent) {
    if (!this.ctx) return;
    const ctx = this.ctx, t = ctx.currentTime;
    if (e === 'arrival') { this.bedsOn = true; this.toll(55, 0.5, 6); this.toll(110, 0.2, 5); void this.startMusic(); }
    if (e === 'caption') { this.pluck(1046 * this.rate, 0.035, 0.6, 'sine', 0.1, t); this.pluck(1568 * this.rate, 0.02, 0.8, 'sine', -0.1, t + 0.03); }
    if (e === 'dive') { this.burst(120, 420, 0.25, 0.09, 0, t); this.roarGain.gain.setTargetAtTime(this.roarGain.gain.value * 1.5 + 0.05, t, 0.05); }
    if (e === 'choice') { this.riser(3.2); this.setTension(true); }
    if (e === 'resolve') { this.setTension(false); }
    if (e === 'hold') { this.setTension(false); this.toll(82.41, 0.25, 4); }
    if (e === 'letgo') { this.setTension(false); this.subDrop(); this.whoosh(); }
    if (e === 'cross') { this.boom(); this.fadeBeds(2.5); }
    if (e === 'ending-hold') {
      this.setTension(false);
      // a lift: raise the filter, add the major third for a few seconds, let the roar go
      this.droneFilter.frequency.setTargetAtTime(3200, t, 1.5);
      this.pluck(138.59, 0.1, 6, 'triangle', 0, t); this.pluck(277.18, 0.05, 6, 'sine', 0, t + 0.1);
      this.roarGain.gain.setTargetAtTime(0.02, t + 1, 2);
    }
  }

  /** Fade the beds out (after the crossing only Earth's tick remains). */
  fadeBeds(seconds: number) {
    this.bedsOn = false;
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    for (const g of [this.subGain, this.droneGain, this.roarGain, this.airGain]) g.gain.setTargetAtTime(0, t, seconds / 3);
    if (this.musicBus) this.musicBus.gain.setTargetAtTime(0, t, seconds / 3);
  }

  /** New voyage: let the score start again on the next arrival. */
  reset() {
    this.bedsOn = true;
    if (this.cur && this.ctx) { this.cur.src.onended = null; try { this.cur.src.stop(this.ctx.currentTime + 0.5); } catch { /* already stopped */ } }
    this.cur = null; this.trackIdx = -1; this.paused = false; this.lastOrbit = 0;
    this.setTension(false);
  }

  // ---------- building blocks ----------
  private setTension(on: boolean) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    if (on && !this.tension) {
      const pedal = this.ctx.createOscillator(); pedal.type = 'sine'; pedal.frequency.value = 659.26 * this.rate;
      const vib = this.ctx.createOscillator(); vib.frequency.value = 5.5; const vibG = this.ctx.createGain(); vibG.gain.value = 6; vib.connect(vibG); vibG.connect(pedal.frequency); vib.start();
      const pedalGain = this.ctx.createGain(); pedalGain.gain.value = 0; pedal.connect(pedalGain); pedalGain.connect(this.beds); pedalGain.connect(this.reverbSend); pedal.start();
      pedalGain.gain.setTargetAtTime(0.05, t, 1.2);
      const dis = this.ctx.createOscillator(); dis.type = 'sawtooth'; dis.frequency.value = 58.27 * this.rate;   // a minor second under the root
      const disGain = this.ctx.createGain(); disGain.gain.value = 0; dis.connect(disGain); disGain.connect(this.droneFilter); dis.start();
      disGain.gain.setTargetAtTime(0.08, t, 2.0);
      this.tension = { pedal, pedalGain, dis, disGain };
    } else if (!on && this.tension) {
      const { pedal, pedalGain, dis, disGain } = this.tension; this.tension = null;
      pedalGain.gain.setTargetAtTime(0, t, 0.4); disGain.gain.setTargetAtTime(0, t, 0.4);
      pedal.stop(t + 3); dis.stop(t + 3);
    }
  }

  private riser(seconds: number) {
    if (!this.ctx || !this.noiseBuf) return;
    const ctx = this.ctx, t = ctx.currentTime;
    const src = ctx.createBufferSource(); src.buffer = this.noiseBuf; src.loop = true;
    const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.Q.value = 1.4; f.frequency.setValueAtTime(260, t); f.frequency.exponentialRampToValueAtTime(5200, t + seconds);
    const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.35, t + seconds); g.gain.exponentialRampToValueAtTime(0.0001, t + seconds + 0.6);
    src.connect(f); f.connect(g); g.connect(this.beds); g.connect(this.reverbSend); src.start(t); src.stop(t + seconds + 0.7);
    const o = ctx.createOscillator(); o.type = 'sawtooth'; o.frequency.setValueAtTime(110 * this.rate, t); o.frequency.exponentialRampToValueAtTime(220 * this.rate, t + seconds);
    const og = ctx.createGain(); og.gain.setValueAtTime(0.0001, t); og.gain.exponentialRampToValueAtTime(0.09, t + seconds); og.gain.exponentialRampToValueAtTime(0.0001, t + seconds + 0.5);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 900;
    o.connect(lp); lp.connect(og); og.connect(this.beds); o.start(t); o.stop(t + seconds + 0.6);
  }

  private subDrop() {
    if (!this.ctx) return;
    const ctx = this.ctx, t = ctx.currentTime;
    const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.setValueAtTime(170, t); o.frequency.exponentialRampToValueAtTime(26, t + 2.4);
    const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.9, t + 0.08); g.gain.exponentialRampToValueAtTime(0.0001, t + 3.6);
    o.connect(g); g.connect(this.beds); o.start(t); o.stop(t + 3.7);
  }

  private whoosh() {
    if (!this.ctx || !this.noiseBuf) return;
    const ctx = this.ctx, t = ctx.currentTime;
    const src = ctx.createBufferSource(); src.buffer = this.noiseBuf; src.loop = true;
    const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.Q.value = 0.9; f.frequency.setValueAtTime(3800, t); f.frequency.exponentialRampToValueAtTime(140, t + 2.8);
    const p = ctx.createStereoPanner(); p.pan.setValueAtTime(-0.9, t); p.pan.linearRampToValueAtTime(0.9, t + 2.8);
    const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.5, t + 0.5); g.gain.exponentialRampToValueAtTime(0.0001, t + 3.2);
    src.connect(f); f.connect(p); p.connect(g); g.connect(this.beds); g.connect(this.reverbSend); src.start(t); src.stop(t + 3.3);
  }

  private boom() {
    if (!this.ctx) return;
    const ctx = this.ctx, t = ctx.currentTime;
    for (const [f, a] of [[30, 1.0], [45, 0.5], [60, 0.25]] as [number, number][]) {
      const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.setValueAtTime(f * 2.2, t); o.frequency.exponentialRampToValueAtTime(f, t + 0.5);
      const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(a, t + 0.04); g.gain.exponentialRampToValueAtTime(0.0001, t + 5.5);
      o.connect(g); g.connect(this.beds); g.connect(this.reverbSend); o.start(t); o.stop(t + 5.6);
    }
    this.burst(40, 400, 0.6, 0.25, 0, t);
  }

  private whomp() {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator(); o.type = 'sine'; o.frequency.setValueAtTime(90, t); o.frequency.exponentialRampToValueAtTime(38, t + 0.35);
    const g = this.ctx.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.45, t + 0.03); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
    o.connect(g); g.connect(this.beds); o.start(t); o.stop(t + 1);
  }

  private toll(freq: number, vol: number, dur: number) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    for (const [mult, a] of [[1, 1], [2.01, 0.35], [3.02, 0.12]] as [number, number][]) {
      const o = this.ctx.createOscillator(); o.type = 'sine'; o.frequency.value = freq * mult;
      const g = this.ctx.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol * a, t + 0.05); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(g); g.connect(this.beds); g.connect(this.reverbSend); o.start(t); o.stop(t + dur + 0.1);
    }
  }

  private pluck(freq: number, vol: number, dur: number, type: OscillatorType, pan: number, t: number) {
    if (!this.ctx) return;
    const o = this.ctx.createOscillator(), g = this.ctx.createGain(), p = this.ctx.createStereoPanner(); p.pan.value = pan;
    o.type = type; o.frequency.value = freq;
    g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(p); p.connect(this.beds); g.connect(this.reverbSend); o.start(t); o.stop(t + dur + 0.05);
  }

  private burst(lo: number, hi: number, vol: number, dur: number, pan: number, t: number) {
    if (!this.ctx || !this.noiseBuf) return;
    const src = this.ctx.createBufferSource(); src.buffer = this.noiseBuf;
    const hp = this.ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = lo; const lp = this.ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = hi;
    const g = this.ctx.createGain(); g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    const p = this.ctx.createStereoPanner(); p.pan.value = pan;
    src.connect(hp); hp.connect(lp); lp.connect(g); g.connect(p); p.connect(this.beds); src.start(t); src.stop(t + dur + 0.02);
  }

  private loop(ctx: AudioContext, buf: AudioBuffer | null): AudioBufferSourceNode {
    const src = ctx.createBufferSource(); src.buffer = buf; src.loop = true; src.start(); return src;
  }

  private makeNoise(ctx: AudioContext, kind: 'white' | 'brown'): AudioBuffer {
    const len = ctx.sampleRate * 3, buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch); let b = 0;
      for (let i = 0; i < len; i++) { const w = Math.random() * 2 - 1; if (kind === 'white') d[i] = w; else { b = (b + 0.02 * w) / 1.02; d[i] = b * 3.5; } }
    }
    return buf;
  }

  private makeImpulse(ctx: AudioContext, seconds: number, decay: number): AudioBuffer {
    const len = Math.floor(ctx.sampleRate * seconds), buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) { const d = buf.getChannelData(ch); for (let i = 0; i < len; i++) { const x = i / len; d[i] = (Math.random() * 2 - 1) * Math.pow(1 - x, 2.2) * Math.exp(-x * decay) * (i < 400 ? i / 400 : 1); } }
    return buf;
  }
}
